import { useEffect, useState } from 'react';
import { clientFetch } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
  },
  progressContainer: {
    marginBottom: '32px',
  },
  progressLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#9CA3AF',
    marginBottom: '8px',
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#1F2937',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#3B82F6',
    transition: 'width 0.3s ease',
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  stepItem: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  stepItemHover: {
    borderColor: '#3B82F6',
    backgroundColor: '#0f172a',
  },
  stepNumber: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#3B82F6',
    minWidth: '32px',
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#FFF',
    marginBottom: '4px',
  },
  stepDescription: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '8px',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgePending: {
    backgroundColor: '#374151',
    color: '#9CA3AF',
  },
  badgeInProgress: {
    backgroundColor: '#fbbf24',
    color: '#1f2937',
  },
  badgeCompleted: {
    backgroundColor: '#10b981',
    color: '#fff',
  },
  completeButton: {
    backgroundColor: '#3B82F6',
    color: '#FFF',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    marginTop: '8px',
    transition: 'background-color 0.2s',
  },
  completeButtonHover: {
    backgroundColor: '#2563eb',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  errorText: {
    color: '#EF4444',
    fontSize: '14px',
  },
};

export default function ClientOnboarding() {
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(null);

  useEffect(() => {
    const fetchOnboarding = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/onboarding');
        setOnboardingData(data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load onboarding');
        console.error('Onboarding error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOnboarding();
  }, []);

  const handleCompleteStep = async (stepId) => {
    try {
      setCompleting(stepId);
      const response = await clientFetch(`/client/portal/onboarding/${stepId}/complete`, {
        method: 'PUT',
      });

      // Update local state
      setOnboardingData((prev) => ({
        ...prev,
        steps: prev.steps.map((step) =>
          step.id === stepId ? { ...step, status: 'completed' } : step
        ),
        progress: response.progress || prev.progress,
      }));
    } catch (err) {
      console.error('Error completing step:', err);
      setError('Failed to mark step as complete');
    } finally {
      setCompleting(null);
    }
  };

  if (loading) {
    return (
      <ClientShell title="Onboarding">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading onboarding checklist...</p>
        </div>
      </ClientShell>
    );
  }

  if (error) {
    return (
      <ClientShell title="Onboarding">
        <div style={styles.container}>
          <p style={styles.errorText}>Error: {error}</p>
        </div>
      </ClientShell>
    );
  }

  const { steps = [], progress = 0 } = onboardingData || {};

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span style={{ ...styles.badge, ...styles.badgeCompleted }}>Completed</span>;
      case 'in_progress':
        return <span style={{ ...styles.badge, ...styles.badgeInProgress }}>In Progress</span>;
      default:
        return <span style={{ ...styles.badge, ...styles.badgePending }}>Pending</span>;
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'in_progress':
        return '→';
      default:
        return steps.indexOf(steps.find((s) => s.id)) + 1;
    }
  };

  return (
    <ClientShell title="Onboarding">
      <div style={styles.container}>
        {/* Progress Bar */}
        <div style={styles.progressContainer}>
          <div style={styles.progressLabel}>Overall Progress: {progress}%</div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
          </div>
        </div>

        {/* Steps List */}
        <div style={styles.stepsList}>
          {steps.map((step, index) => (
            <div
              key={step.id}
              style={styles.stepItem}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3B82F6';
                e.currentTarget.style.backgroundColor = '#0f172a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#1F2937';
                e.currentTarget.style.backgroundColor = '#111827';
              }}
            >
              <div style={styles.stepNumber}>{index + 1}</div>
              <div style={styles.stepContent}>
                <div style={styles.stepTitle}>{step.title}</div>
                <div style={styles.stepDescription}>{step.description}</div>
                <div>
                  {getStatusBadge(step.status)}
                  {step.status !== 'completed' && (
                    <button
                      style={styles.completeButton}
                      onClick={() => handleCompleteStep(step.id)}
                      disabled={completing === step.id}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#2563eb';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#3B82F6';
                      }}
                    >
                      {completing === step.id ? 'Marking...' : 'Mark Complete'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ClientShell>
  );
}
